import { PaginatedSelector } from '@chewy/chat-library-components';
import OrderCardPackage from './OrderCardPackage';
import type { ChatMessage, PackageCardData, RequestScrollCallback } from '@chewy/chat-library-common';
const MAX_PACKAGES_PER_PAGE = 3;

interface OrderCardPackageSelectorProps {
  /**
   * Array of package data to display
   */
  data: PackageCardData[];
  /**
   * Layout direction for the cards
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Chat message associated with the packages
   */
  message?: ChatMessage;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Page offset for the cards
   * @default 3
   */
  pageOffset?: number;
  /**
   * Callback function to request scrolling to a selected item
   */
  requestScroll?: RequestScrollCallback;
}

/**
 * Displays a list of selectable OrderCardPackage components with pagination
 * Uses PaginatedSelector to handle pagination and action buttons
 */
export default function OrderCardPackageSelector({
  data,
  direction = 'vertical',
  message,
  className,
  pageOffset = MAX_PACKAGES_PER_PAGE,
  requestScroll
}: OrderCardPackageSelectorProps) {
  return (
    <PaginatedSelector
      data={data}
      renderItem={(pkg, index, { ref, handleSelect }) => {
        return (
          <OrderCardPackage
            key={pkg.orderId ?? index}
            ref={ref}
            data={[{ ...pkg, packageNumber: `${index + 1}`, totalPackages: data.length }]}
            onSelect={() => handleSelect(message?.responseTimestamp)}
          />
        );
      }}
      direction={direction}
      className={className}
      pageSize={pageOffset}
      itemType="package"
      requestScroll={requestScroll}
      showMoreAction={[{
        defaultAction: {
          type: 'button',
          text: 'Show more',
          payload: {
            action: 'SHOW_MORE_PACKAGES',
          },
        },
      }]}
      secondaryAction={[{
        defaultAction: {
          type: 'button',
          text: 'Need help with a different package',
          payload: {
            action: 'SHOW_DIFFERENT_PACKAGE_OPTIONS',
          },
        },
      }]}
    />
  );
}
